import * as Telecom from '@servicetitan/telecom-rtcs-client';
import { userStatsFromAgentStats } from '../../types/ServiceUser';
import appManager from '@app/appManager';
import {
  ServiceSessionEventType,
  ServiceSessionOfferedEvent,
} from '../../types/ServiceEvents';
import requireUser, { rosterUser } from '../../lib/requireUser';
import log from '../../lib/log';
import { getSession, isOfferedSessionDeleted } from '../../lib/sessions';
import { serviceState } from '../../lib/state';
import getTenantId from '../../lib/getTenantId';
import isValidUser from '../../lib/isValidUser';
import { EventAvailabilityState } from '../../lib/eventAvailabilityState';
import { ServiceUserUtils } from '../../lib/serviceUserUtils';
import getQueue from '../../lib/getQueue';
import { RTCSEventType } from '../../../metrics/rtcsMetrics';
import { trackRTCSEventLatency } from '../../lib/trackRTCSEventLatency';
import { getLiveServicesTenantId } from '../../lib/liveServicesMapping';
import {
  applyAgentSessionNow,
  enqueueNonSelfAgentSessionEvent,
  shouldBufferNonSelfAgentSession,
} from '../../lib/coalesceWorkspaceEvents';

function queueTimeoutFromOffer(
  event: Telecom.AgentSessionOfferedEvent,
): number | undefined {
  if (!event.queueId || !event.queueEnterTime) {
    return undefined;
  }
  const timeout = getQueue(event.queueId)?.rolloverSettings?.timeout;
  if (!timeout) {
    return undefined;
  }
  try {
    const enterTime = new Date(event.queueEnterTime).getTime();
    const timeParts = timeout.split(':').map(Number);
    if (timeParts.length !== 3 || timeParts.some((part) => isNaN(part))) {
      return undefined;
    }
    const [hours, minutes, seconds] = timeParts;
    const timeoutSeconds = hours * 3600 + minutes * 60 + seconds;
    if (isNaN(enterTime) || timeoutSeconds <= 0) {
      return undefined;
    }
    return enterTime + timeoutSeconds * 1000;
  } catch (error) {
    console.error('Failed to calculate queue timeout:', error);
    return undefined;
  }
}

function tenantIdFromOffer(event: Telecom.AgentSessionOfferedEvent) {
  return serviceState.isLiveServicesNetwork
    ? getLiveServicesTenantId(
        event.tenantId,
        event.target.address,
        serviceState.liveServicesMappings,
      )
    : getTenantId(event.tenantId);
}

/**
 * The offer event indicates that a session is being offered to
 * the recipient from a queue.
 * @param event
 */
async function prepareAgentSessionOffered(
  event: Telecom.AgentSessionOfferedEvent,
) {
  if (!isValidUser(event.agentId)) {
    return undefined;
  }

  const user = await requireUser(event.agentId);

  if (isOfferedSessionDeleted(event.conversationSid, event.timestamp)) {
    console.warn('offered session is already deleted', event.conversationId);
    return undefined;
  }

  return {
    user,
    tenantId: tenantIdFromOffer(event),
    queueTimeout: queueTimeoutFromOffer(event),
  };
}

function commitAgentSessionOffered(
  event: Telecom.AgentSessionOfferedEvent,
  prepared: NonNullable<Awaited<ReturnType<typeof prepareAgentSessionOffered>>>,
) {
  if (isOfferedSessionDeleted(event.conversationSid, event.timestamp)) {
    console.warn('offered session is already deleted', event.conversationId);
    return;
  }

  const { tenantId, queueTimeout } = prepared;
  const user = rosterUser(event.agentId, prepared.user);
  log('rtcs:agent:session_offered:' + user.name, event);

  user.stats = userStatsFromAgentStats(event.stats);

  ServiceUserUtils.setEventActivityStatus(
    user,
    EventAvailabilityState.CallOffer,
  );

  let session = getSession(event.conversationId);
  if (!session) {
    console.warn(
      'offered session was not stored previously',
      event.conversationId,
    );
    serviceState.sessions[event.conversationId] = {
      ...event,
      createdAt: Date.now(),
      tenantId,
    };
  }

  session = serviceState.sessions[event.conversationId];
  session.queueId = event.queueId;
  session.queueName = event.queueName;
  session.queueEnterTime = event.queueEnterTime;
  session.target = event.target;
  session.tenantId = tenantId;
  if (queueTimeout !== undefined) {
    session.queueTimeout = queueTimeout;
  }

  appManager.events.emit<ServiceSessionOfferedEvent>(
    ServiceSessionEventType.OFFERED,
    {
      sessionId: event.conversationId,
      sessionSid: event.conversationSid,
      agentId: event.agentId,
      userId: user.id,
      from: event.source.address,
      to: event.target.address,
      displayName: event.source.displayName ?? '',
      tenantId,
      queueId: event.queueId,
      queueName: event.queueName,
      queueEnterTime: event.queueEnterTime,
      queueTimeout,
      source: event.source,
    },
  );
}

async function handleAgentSessionOffered(
  event: Telecom.AgentSessionOfferedEvent,
) {
  // Receive time, not flush time — coalesced non-self apply is ~1s later.
  trackRTCSEventLatency(event.timestamp, RTCSEventType.SESSION_OFFERED);
  if (!shouldBufferNonSelfAgentSession(event.agentId)) {
    return applyAgentSessionNow(event.agentId, (user) => {
      if (isOfferedSessionDeleted(event.conversationSid, event.timestamp)) {
        console.warn(
          'offered session is already deleted',
          event.conversationId,
        );
        return;
      }
      commitAgentSessionOffered(event, {
        user,
        tenantId: tenantIdFromOffer(event),
        queueTimeout: queueTimeoutFromOffer(event),
      });
    });
  }
  return enqueueNonSelfAgentSessionEvent(event.agentId, {
    prepare: () => prepareAgentSessionOffered(event),
    commit: (value) => commitAgentSessionOffered(event, value),
  });
}

export default handleAgentSessionOffered;
